// Helpers for the PR Discussion tab. The API contract lives in
// docs/pr-discussion-tab-backend-spec-updated.md, amended by the backend's
// delivery note (integer quantities, the real pr_status vocabulary).

import { isFeatureDisabled } from "./notifications";

export { isFeatureDisabled };

export const COMMENT_PAGE_SIZE = 20;

export const QTY_REDUCTION = "QTY_REDUCTION";
export const SYSTEM = "SYSTEM";

// How long the author may edit their own comment. The server enforces this; the
// number here only drives the client-side countdown when the API omits
// editable_until, so the Edit button disappears instead of erroring.
export const EDIT_WINDOW_MINUTES = 15;

export const MAX_COMMENT_LENGTH = 2000;

// messageCode -> human copy. Anything unmapped falls back to the server message.
const DISCUSSION_MESSAGES = {
  NOT_FOUND: "That record no longer exists — refresh and try again.",
  FORBIDDEN: "You don't have permission to do that.",
  EMPTY_MESSAGE: "Comment can't be empty.",
  MESSAGE_TOO_LONG: `Comment is too long (max ${MAX_COMMENT_LENGTH} characters).`,
  EDIT_WINDOW_EXPIRED:
    "Comments can only be edited within 15 minutes of posting.",
  IMMUTABLE_FIELD:
    "That part of the request can't be edited — cancel it and raise a new one.",
  INVALID_QTY:
    "Requested quantity must be lower than the current quantity.",
  INVALID_ITEM: "That item isn't on this requisition.",
  DUPLICATE_PENDING:
    "There's already a pending reduction for this item.",
  ALREADY_DECIDED: "This request has already been decided.",
  // Quantity and unit price are both staleness tokens: a reduction agreed at one
  // price can't be applied at another.
  STALE_REDUCTION:
    "This line's quantity or price changed after the request was made. Withdraw it and raise a new one.",
  OUT_OF_SCOPE: "You don't have access to this requisition's discussion.",
  FEATURE_DISABLED: "Discussion isn't available for this company yet.",
  PR_LOCKED: "This requisition can't be changed at its current stage.",
};

export const getDiscussionMessage = (messageCode, fallbackMessage) =>
  DISCUSSION_MESSAGES[messageCode] ||
  fallbackMessage ||
  "Something went wrong.";

// blocked_reason is a closed set, and it only ever explains the *reduction*
// gate — commenting is never status-blocked. An unknown code deliberately yields
// no tooltip rather than a guess.
const BLOCKED_REASONS = {
  PR_DRAFT: "The requisition hasn't been submitted yet.",
  PR_APPROVED:
    "Quantity reductions are only available while the requisition is pending approval.",
  PR_REJECTED: "This requisition was rejected.",
  PR_IN_PROCUREMENT: "This requisition has already gone out to suppliers.",
  PR_CLOSED: "This requisition is closed.",
  // The server's fallback for a pr_status it doesn't recognise.
  PR_LOCKED: "Quantity reductions aren't available at this stage.",
  // Unreachable until Cancel PR ships — nothing writes a CANCELLED pr_status yet.
  PR_CANCELLED: "This requisition was cancelled.",
};

export const blockedReasonCopy = (code) => BLOCKED_REASONS[code] || null;

// created_at arrives as UTC. A naive string (no zone) is still UTC per the spec,
// so it needs the Z or the browser reads it as local time — the same trap the
// notification feed hit.
export const parseUtc = (value) => {
  if (!value) return null;

  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const date = new Date(hasZone ? value : `${value}Z`);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const normalizeComment = (comment = {}) => ({
  ...comment,
  id: Number(comment.id),
  type: comment.type || "COMMENT",
  can_edit: Boolean(comment.can_edit),
  can_delete: Boolean(comment.can_delete),
  qty_reduction: comment.qty_reduction
    ? {
        ...comment.qty_reduction,
        id: Number(comment.qty_reduction.id),
        status: String(comment.qty_reduction.status || "").toUpperCase(),
        can_decide: Boolean(comment.qty_reduction.can_decide),
      }
    : null,
});

// Newest first, matching the server's `created_at DESC, id DESC`. The id tiebreak
// is not cosmetic: without it two comments sharing a timestamp can swap places
// between pages and appear twice.
const byNewestFirst = (a, b) => {
  const diff =
    (parseUtc(b.created_at)?.getTime() || 0) -
    (parseUtc(a.created_at)?.getTime() || 0);

  return diff !== 0 ? diff : b.id - a.id;
};

// A refetch and an optimistic insert delivering the same row is normal — id is
// stable, so dedupe on it and let the incoming copy win.
export const mergeComments = (existing = [], incoming = []) => {
  const byId = new Map(existing.map((row) => [row.id, row]));

  incoming.forEach((row) => {
    const normalized = normalizeComment(row);
    byId.set(normalized.id, normalized);
  });

  return [...byId.values()].sort(byNewestFirst);
};

// The store keeps rows newest-first (server order); the thread reads oldest-first.
export const oldestFirst = (rows = []) => [...rows].reverse();

const addMinutes = (date, minutes) =>
  date ? new Date(date.getTime() + minutes * 60000) : null;

// can_edit is computed when the page loads, so a comment fetched at minute 14
// would otherwise keep showing an Edit button that fails at minute 16.
export const canEditNow = (comment, now = new Date()) => {
  if (!comment?.can_edit) return false;

  const deadline = comment.editable_until
    ? parseUtc(comment.editable_until)
    : addMinutes(parseUtc(comment.created_at), EDIT_WINDOW_MINUTES);

  // No usable timestamp at all: trust the server's flag rather than hiding a
  // control the user is entitled to.
  if (!deadline) return true;

  return deadline.getTime() > now.getTime();
};

export const isQtyReduction = (comment) => comment?.type === QTY_REDUCTION;
export const isSystem = (comment) => comment?.type === SYSTEM;

// Whether a row has a person behind it. Branch on author_id, not type: SYSTEM
// comes in two kinds — accept/reject narratives carry the decider as author,
// while auto-cancel rows (PR closed with a reduction still pending) have
// author_id, author_name and author_role all null.
export const isAuthorless = (comment) => comment?.author_id == null;

// author_name can also be "" on an ordinary comment, for a user whose first and
// last name are both blank — so fall back on falsy, not just null.
export const authorLabel = (comment) => {
  if (isAuthorless(comment)) return "System";

  return String(comment.author_name ?? "").trim() || "Unknown user";
};

// Line quantities are integers server-side, and a reduction to zero would mean
// deleting the line — a different operation. So the floor is 1.
export const MIN_REDUCED_QTY = 1;

// An item already at the floor has nothing valid to reduce to.
export const canReduceItem = (item) =>
  Number(item?.quantity) > MIN_REDUCED_QTY;

// Mirrors the server's INVALID_QTY rule so the user finds out before the round trip.
export const reductionQtyError = (requestedQty, originalQty) => {
  if (requestedQty === "" || requestedQty == null) {
    return "Enter the new quantity.";
  }

  const qty = Number(requestedQty);

  if (!Number.isInteger(qty)) return "Quantity must be a whole number.";
  if (qty < MIN_REDUCED_QTY) {
    return `Quantity must be at least ${MIN_REDUCED_QTY}.`;
  }
  if (originalQty != null && qty >= Number(originalQty)) {
    return `Must be lower than the current quantity (${originalQty}).`;
  }

  return null;
};

